import React from 'react';
import { motion } from 'framer-motion';
import { Users, Award, Globe, Clock, Briefcase, Search, GraduationCap, FileText, ClipboardCheck, Network, Building2 } from 'lucide-react';
import { independentRecruiters } from '../data/independentRecruiters';
export function AboutPage() {
  return <div className="pt-20" data-id="element-99" data-mp="5nxzo0yd0zv">
      {/* Hero */}
      <section className="bg-navy-900 text-white py-20" data-id="element-100" data-mp="4wdb88w8nod">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-101" data-mp="3re38siqjluze">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-102" data-mp="3mdfdvlz70tzw">
            About Continuum Talent Solutions
          </motion.h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-103" data-mp="1gkgvpags45t4">
            We are more than just recruiters. We are career architects dedicated
            to building the future of work, one placement at a time.
          </p>
        </div>
      </section>

      {/* About Continuum Talent Solutions — Company Overview */}
      <section className="py-20 bg-white" data-id="element-104" data-mp="2jsxxiaulq8qn">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-105" data-mp="25wkq5ujs7npw">
          {/* Section header */}
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-106" data-mp="2avnkajy849j3">
            <span className="inline-block text-gold-600 font-semibold tracking-[0.25em] uppercase text-xs mb-4" data-id="element-107" data-mp="2yk6q8stswa80">
              Who We Are
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-6" data-id="element-108" data-mp="32la9lpb24odz">
              About Continuum Talent Solutions
            </h2>
            <div className="w-16 h-0.5 bg-gold-500 mx-auto mb-8" data-id="element-109" data-mp="2tt87lmlcb01l" />
            <p className="text-lg text-gray-700 leading-relaxed" data-id="element-110" data-mp="2trdmo8a2j7xi">
              At Continuum Talent Solutions, we connect exceptional talent with
              forward-thinking organizations across diverse industries
              worldwide. We are a people-driven recruitment and workforce
              solutions company committed to helping businesses build
              high-performing teams while empowering professionals to achieve
              meaningful career success.
            </p>
          </div>

          {/* Body paragraphs */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mb-20" data-id="element-111" data-mp="3h9a7fbfzjh43">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="border-l-4 border-gold-500 pl-6" data-id="element-112" data-mp="3d7fbkmgtpvxp">
              <p className="text-gray-600 leading-relaxed" data-id="element-113" data-mp="2yfwm7y22qp1o">
                Our expertise extends beyond traditional recruitment. We
                specialize in talent acquisition, executive search, resume and
                LinkedIn optimization, career consulting, interview preparation,
                workforce documentation support, and strategic hiring solutions
                tailored to the evolving demands of today's global marketplace.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="border-l-4 border-gold-500 pl-6" data-id="element-114" data-mp="1dxvttv9g7a6q">
              <p className="text-gray-600 leading-relaxed" data-id="element-115" data-mp="zzxheveotntv">
                With a strong understanding of industry trends and employer
                expectations, we partner with companies to identify top-tier
                professionals who drive innovation, productivity, and long-term
                growth. For job seekers, we provide personalized career support
                designed to increase visibility, strengthen professional
                branding, and unlock new opportunities.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="border-l-4 border-gold-500 pl-6" data-id="element-116" data-mp="2izna4t1vcnb2">
              <p className="text-gray-600 leading-relaxed" data-id="element-117" data-mp="31vn3a76l1ww8">
                At Continuum Talent Solutions, we believe recruitment is more
                than filling positions — it is about building lasting
                relationships, creating career pathways, and bridging the gap
                between talent and opportunity.
              </p>
            </motion.div>
          </div>

          {/* What We Deliver */}
          <div className="bg-gray-50 rounded-2xl p-8 md:p-12" data-id="element-118" data-mp="3j6sd3i4ndxso">
            <div className="text-center mb-12" data-id="element-119" data-mp="3j6acyaev6zfh">
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-3" data-id="element-120" data-mp="35g6qpjfzj5q1">
                What We Deliver
              </h3>
              <div className="w-12 h-0.5 bg-gold-500 mx-auto" data-id="element-121" data-mp="39ffnkx6bnjpt" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-122" data-mp="2vgprnkv8nq31">
              {[{
              icon: Briefcase,
              label: 'Executive & Professional Recruitment'
            }, {
              icon: Search,
              label: 'Talent Sourcing & Acquisition'
            }, {
              icon: GraduationCap,
              label: 'Career Coaching & Job Search Support'
            }, {
              icon: FileText,
              label: 'Resume Writing & LinkedIn Optimization'
            }, {
              icon: ClipboardCheck,
              label: 'Workforce & Documentation Assistance'
            }, {
              icon: Globe,
              label: 'Remote & Global Hiring Solutions'
            }, {
              icon: Building2,
              label: 'Employer Branding & Staffing Support'
            }, {
              icon: Network,
              label: 'Strategic Hiring Solutions'
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="flex items-center gap-4 bg-white p-5 rounded-lg shadow-sm border border-gray-100" data-id="element-123" data-mp="27kba31qdnfd3" data-mpi={index}>
                  <div className="flex-shrink-0 w-11 h-11 rounded-full bg-navy-900 flex items-center justify-center" data-id="element-124" data-mp="3cjhkmzfdpsee">
                    <item.icon className="h-5 w-5 text-gold-500" data-id="element-125" data-mp="3gkjknih4e8hf" />
                  </div>
                  <span className="text-gray-800 font-medium text-sm leading-snug" data-id="element-126" data-mp="3llh2fty7x0ie">
                    {item.label}
                  </span>
                </motion.div>)}
            </div>
          </div>

          {/* Values + tagline */}
          <div className="mt-16 max-w-4xl mx-auto text-center" data-id="element-127" data-mp="45r682zh60if">
            <p className="text-gray-600 leading-relaxed mb-8 text-lg" data-id="element-128" data-mp="4724xoe2ci2o">
              We are dedicated to{' '}
              <span className="text-navy-900 font-semibold" data-id="element-129" data-mp="d5xf1bk8acb9">
                professionalism, integrity, innovation, and results
              </span>{' '}
              — helping both organizations and professionals thrive in a
              competitive world.
            </p>
            <div className="inline-block" data-id="element-130" data-mp="tjvr0um4tvyt">
              <p className="text-2xl md:text-3xl font-serif italic text-navy-900 mb-2" data-id="element-131" data-mp="18fld0srbi93j">
                Continuum Talent Solutions
              </p>
              <p className="text-gold-600 font-medium tracking-wider text-sm uppercase" data-id="element-132" data-mp="13gqqbfq8c0e5">
                Connecting Talent with Opportunity
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gray-50" data-id="element-133" data-mp="pgpj7ijdfwq8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134" data-mp="philo7bas02k">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center" data-id="element-135" data-mp="kog0onkl5yxz">
            <div data-id="element-136" data-mp="3t4r8rlqmx0gm">
              <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Team collaboration" className="rounded-lg shadow-xl" data-id="element-137" data-mp="3t496o8hajs0s" />
            </div>
            <div data-id="element-138" data-mp="28elxvftm19pe">
              <h2 className="text-3xl font-serif font-bold text-navy-900 mb-6" data-id="element-139" data-mp="28f5camk1xfyo">
                Our Mission
              </h2>
              <p className="text-gray-600 mb-8 text-lg leading-relaxed" data-id="element-140" data-mp="1trke9721a6xm">
                To empower professionals to achieve their full potential and
                assist organizations in building world-class teams. We believe
                that the right job can transform a life, and the right employee
                can transform a business.
              </p>

              <h2 className="text-3xl font-serif font-bold text-navy-900 mb-6" data-id="element-141" data-mp="2deqc0z71pscg">
                Our Vision
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed" data-id="element-142" data-mp="2hdz6655zzs8j">
                To be the global standard in recruitment excellence, known for
                integrity, speed, and the quality of our connections.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Grid */}
      <section className="bg-white py-20" data-id="element-143" data-mp="1qpnf9e2u5jni">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-144" data-mp="1vn6wmmutycjo">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-145" data-mp="1vohwibl5n05i">
            {[{
            icon: Users,
            label: 'Expert Recruiters',
            value: '50+'
          }, {
            icon: Award,
            label: 'Awards Won',
            value: '15'
          }, {
            icon: Globe,
            label: 'Global Offices',
            value: '6'
          }, {
            icon: Clock,
            label: 'Years Active',
            value: '12'
          }].map((stat, index) => <div key={index} className="bg-white p-8 rounded-lg shadow-sm text-center" data-id="element-146" data-mp="2tbgxgyivqu97" data-mpi={index}>
                <stat.icon className="h-8 w-8 text-gold-500 mx-auto mb-4" data-id="element-147" data-mp="2x9wrvnhaj4wn" />
                <div className="text-3xl font-bold text-navy-900 mb-2" data-id="element-148" data-mp="lpgry1h2zhex">
                  {stat.value}
                </div>
                <div className="text-gray-600" data-id="element-149" data-mp="hkzdmhthpwbj">{stat.label}</div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section id="team" className="py-20 scroll-mt-24" data-id="element-150" data-mp="qmyv1b8f32f0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-151" data-mp="15cejuloe0xs2">
          <div className="text-center mb-16" data-id="element-152" data-mp="15er9ll0e0cfo">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy-900 mb-4" data-id="element-153" data-mp="10e3byghnkxch">
              Meet Our Team
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-154" data-mp="1ect6pph02kxo">
              Experienced professionals dedicated to transforming careers and
              connecting talent with opportunity.
            </p>
          </div>

          {/* Founder — Featured */}
          <div className="flex flex-col items-center mb-20" data-id="element-155" data-mp="3b64q7my2khf3">
            <div className="relative group" data-id="element-156" data-mp="3b7pnvlfokgh6">
              {/* Decorative ring */}
              <div className="absolute -inset-3 rounded-full border-2 border-gold-500/40 animate-pulse" data-id="element-157" data-mp="1f4b3c9c8bcx8" />
              <div className="absolute -inset-6 rounded-full border border-gold-500/20" data-id="element-158" data-mp="1k1tc9zq8xs58" />
              {/* Photo circle */}
              <div className="relative w-48 h-48 rounded-full overflow-hidden border-4 border-gold-500 shadow-2xl" data-id="element-159" data-mp="1t29i3fugpbd9">
                <img src="https://cdn.magicpatterns.com/uploads/qdYs7LErqNJjHmXmoY5vKY/1636567611328_(1).jpg" alt="Michael Johnson — Founder & CEO" className="w-full h-full object-cover" data-id="element-160" data-mp="1y1uf3kq2n9nc" />
              </div>
              {/* Gold badge */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-gold-500 text-white text-xs font-bold px-4 py-1 rounded-full shadow-md tracking-wider uppercase whitespace-nowrap" data-id="element-161" data-mp="2cqi93mk9rbb6">
                Founder & CEO
              </div>
            </div>
            <div className="mt-10 text-center" data-id="element-162" data-mp="2lokd8azauk2o">
              <h3 className="text-2xl font-serif font-bold text-navy-900 mb-1" data-id="element-163" data-mp="a4mkggnaiv7n">
                Michael Johnson
              </h3>
              <p className="text-gold-600 font-semibold mb-3" data-id="element-164" data-mp="535q73zba701">
                Founder & Chief Executive Officer
              </p>
              <p className="text-gray-500 max-w-md mx-auto text-sm leading-relaxed" data-id="element-165" data-mp="3s9g48r35po8p">
                Michael founded Continuum Talent Solutions to create more
                meaningful connections between exceptional professionals and the
                teams that need them most.
              </p>
            </div>
          </div>

          {/* Team Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10" data-id="element-166" data-mp="jzwqdfgt7mqp">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/gZ8sRYjABDAWW9NXjHvXZx/1518079433608.jpg',
            name: 'Mitch B',
            role: 'Retained Executive Search Consultant'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qgHzMdd3A2XcVSTqC8WZwX/1677800762668.jpg',
            name: 'Adam Cray',
            role: 'Executive Recruiter'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/b21mh5k9WS1NLv2czZhqk6/1715370377328.jpg',
            name: 'Abel (AJ) Davila',
            role: 'Executive Search Consultant'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/8sDh3tnZgXHXHhgo59bW2V/Brian_Waldrop.jpg',
            name: 'Brian Waldrop',
            role: 'Executive Search Consultant'
          }].map((member, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="flex flex-col items-center text-center" data-id="element-167" data-mp="168too2jljbg9" data-mpi={index}>
                <div className="relative mb-5" data-id="element-168" data-mp="2huizzj7e4gau">
                  <div className="w-36 h-36 rounded-full overflow-hidden border-4 border-white shadow-xl ring-2 ring-navy-900/10" data-id="element-169" data-mp="2ht7vtia196j7">
                    <img src={member.img} alt={member.name} className="w-full h-full object-cover" data-id="element-170" data-mp="1p52c8yuptnky" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-1" data-id="element-171" data-mp="1z4jfl9mhvpdr">
                  {member.name}
                </h3>
                <p className="text-gold-600 font-medium text-sm" data-id="element-172" data-mp="1u2u5bw73jfqq">
                  {member.role}
                </p>
              </motion.div>)}
          </div>

          {/* Independent Recruiter Network */}
          <div className="mt-24 border-t border-gray-200 pt-16" data-id="element-173" data-mp="35lojq9ggbsqf">
            <div className="max-w-2xl mb-10" data-id="element-174" data-mp="3dji1ahdz97rj">
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-navy-900 mb-3" data-id="element-175" data-mp="3hlv3wwxfgpk3">
                Independent Recruiter Network
              </h3>
              <p className="text-gray-600" data-id="element-176" data-mp="3mlhjhd0qahw2">
                Twenty independent executive recruiters extend our reach across
                industries and markets, each bringing specialist depth to the
                searches they lead.
              </p>
            </div>

            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-1" data-id="element-177" data-mp="2yulxwxmv8ix9">
              {independentRecruiters.map(recruiter => <li key={recruiter.name} className="flex items-center gap-4 py-3 border-b border-gray-100" data-id="element-178" data-mp="33snd37h67y3g">
                  <span aria-hidden="true" className="flex-shrink-0 w-10 h-10 rounded-full bg-navy-900 text-gold-500 flex items-center justify-center text-xs font-bold tracking-wide" data-id="element-179" data-mp="2b4zyqa6hu3p9">
                    {recruiter.initials}
                  </span>
                  <span className="min-w-0" data-id="element-180" data-mp="23mtbkbkd7j9s">
                    <span className="block font-semibold text-navy-900 text-sm truncate" data-id="element-181" data-mp="1ymx8qfnelxvt">
                      {recruiter.name}
                    </span>
                    <span className="block text-xs text-gray-500 truncate" data-id="element-182" data-mp="1tnkps5znlayi">
                      {recruiter.specialty} · {recruiter.region}
                    </span>
                  </span>
                </li>)}
            </ul>
          </div>
        </div>
      </section>
    </div>;
}